import {useMemo, useState} from "react";
import type {CSSProperties} from "react";
import {Link, useLoaderData, useNavigate} from "react-router-dom";
import type {GetAppInfoResult, GetMunicipalitiesResult} from "../../api";
import {logoUrl} from "../../utils/logoUrl";
import {Trans, useTranslation} from "react-i18next";
import "./AllMunicipalitiesHome.css";
import {useFormatters} from "../../utils/texts";
import {useLocalizationLabels} from "../../context/LocalizationLabelContext";
import {useGlobalStats} from "../../hooks/useGlobalStats";
import {AutoComplete, Button, Card, Divider, Skeleton} from "../../components/PrimereactComponents";
import ContentSection from "../../components/common/ContentSection/ContentSection";
import {BROWSE_GROUPS, resolveBrowseGroup} from "../../utils/municipalityGroups";
import type {BrowseGroup} from "../../utils/municipalityGroups";

type Municipality = GetMunicipalitiesResult[number];
type SearchableMunicipality = Municipality & {label: string};

const VOTE_BOARD_COLUMNS = 24;
const VOTE_BOARD_ROWS = 5;
// Matches the backend's HlasovaciMoznost enum (api/orm/schema_enums.py: YES=1,
// NO=2, ABSTAIN=3, …) and the same YES/NO/ABSTAIN → --clr-1/2/3 mapping already
// used by useVoteColors() elsewhere in the app — not an invented color scheme.
const VOTE_KINDS = ["YES", "NO", "ABSTAIN"] as const;
const MAX_PULSING_CELLS = 4;

type BoardCell = {kind: typeof VOTE_KINDS[number] | null; pulse: boolean};

// Decorative "vote tally board" background for the hero — purely ambient.
const buildVoteBoard = (): BoardCell[] => {
    let pulses = 0;
    return Array.from({length: VOTE_BOARD_COLUMNS * VOTE_BOARD_ROWS}, () => {
        if (Math.random() >= 0.16) {
            return {kind: null, pulse: false};
        }
        const kind = VOTE_KINDS[Math.floor(Math.random() * VOTE_KINDS.length)];
        const pulse = kind === "YES" && pulses < MAX_PULSING_CELLS && Math.random() < 0.3;
        if (pulse) {
            pulses += 1;
        }
        return {kind, pulse};
    });
};

type StatTileProps = {
    value: number | undefined;
    label: string;
    loading: boolean;
    format: (value: number) => string;
};

const StatTile = ({value, label, loading, format}: StatTileProps) => (
    <div className="stat-tile">
        {loading
            ? <Skeleton width="4.5rem" height="2rem" />
            : <span className="stat-value">{format(value ?? 0)}</span>
        }
        <span className="stat-label">{label}</span>
    </div>
);

const GroupStatLine = ({value, label, loading, format}: StatTileProps) => (
    <li className="home-group-stat-line">
        {loading
            ? <Skeleton width="3.5rem" height="1.1rem" />
            : <strong>{format(value ?? 0)}</strong>
        }
        {" "}{label}
    </li>
);

const AllMunicipalitiesHome = () => {
    const {t, i18n} = useTranslation("mainHome");
    const navigate = useNavigate();
    const responses = useLoaderData() as [GetMunicipalitiesResult, GetAppInfoResult];
    const municipalities = responses[0];

    const {municipalityNameOptionLabels} = useLocalizationLabels();

    const formatter = useFormatters(i18n.language);
    const {stats, loading: statsLoading} = useGlobalStats(municipalities);
    const voteBoard = useMemo(buildVoteBoard, []);

    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<SearchableMunicipality[]>([]);

    const municipalityLabel = (item: Municipality) =>
        municipalityNameOptionLabels[item.id]?.label ?? item.text_id;

    const searchableItems = useMemo<SearchableMunicipality[]>(
        () => municipalities.map((item) => ({...item, label: municipalityLabel(item)})),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [municipalities, municipalityNameOptionLabels]
    );

    const quickPicks = useMemo(
        () => searchableItems.filter((item) => item.partner).slice(0, 3),
        [searchableItems]
    );

    const goToMunicipality = (item: SearchableMunicipality) => navigate(`/${item.text_id}`);

    const search = (event: {query: string}) => {
        const term = event.query.trim().toLowerCase();
        setSuggestions(
            term
                ? searchableItems.filter((item) =>
                    item.label.toLowerCase().includes(term) || item.text_id.toLowerCase().includes(term)
                ).slice(0, 8)
                : searchableItems.slice(0, 8)
        );
    };

    const grouped = municipalities.reduce((acc: Record<BrowseGroup, Municipality[]>, item: Municipality) => {
        const key = resolveBrowseGroup(item.typ_code);
        acc[key].push(item);
        return acc;
    }, {kraj: [], mesto: [], national: []} as Record<BrowseGroup, Municipality[]>);

    const groupLabels: Record<BrowseGroup, string> = {
        kraj: t("Browse.groupRegions"),
        mesto: t("Browse.groupCities"),
        national: t("Browse.groupNational")
    };

    return (<>
        <Card className="home-hero-card">
            <div className="home-hero-board" aria-hidden="true">
                {voteBoard.map((cell, index) => (
                    <span
                        key={index}
                        className={[
                            "board-cell",
                            cell.kind ? `board-cell--${cell.kind.toLowerCase()}` : "",
                            cell.pulse ? "board-cell--pulse" : ""
                        ].filter(Boolean).join(" ")}
                    />
                ))}
            </div>

            <div className="home-hero-content">
                <span className="home-hero-eyebrow">{t("Hero.eyebrow")}</span>
                <h1>{t("headline")}</h1>
                <p className="home-hero-lede">
                    <Trans i18nKey="intro" ns="mainHome" components={{b: <b />}} />
                </p>

                <AutoComplete
                    value={query}
                    suggestions={suggestions}
                    completeMethod={search}
                    field="label"
                    appendTo="self"
                    onChange={(event: {value: string}) => setQuery(event.value)}
                    onSelect={(event: {value: SearchableMunicipality}) => goToMunicipality(event.value)}
                    itemTemplate={(item: SearchableMunicipality) => (
                        <div className="home-search-item">
                            <img src={logoUrl(item.text_id)} alt="" className="home-search-item-logo" />
                            <span>{item.label}</span>
                        </div>
                    )}
                    placeholder={t("Hero.searchPlaceholder")}
                    emptyMessage={t("Hero.searchEmpty")}
                    showEmptyMessage
                    inputClassName="home-search-input"
                    panelClassName="home-search-panel"
                    className="home-search"
                    dropdown={false}
                />

                {quickPicks.length > 0 &&
                    <div className="home-quickpicks">
                        <span className="home-quickpicks-label">{t("Hero.quickPicksLabel")}</span>
                        {quickPicks.map((item) => (
                            <Button
                                key={item.id}
                                label={item.label}
                                className="p-button-text p-button-sm home-quickpick-btn"
                                onClick={() => goToMunicipality(item)}
                            />
                        ))}
                    </div>
                }
            </div>

            <Divider className="home-hero-divider" />

            <div className="home-scoreboard-row">
                <StatTile value={municipalities.length} label={t("Stats.municipalities")} loading={false} format={formatter.number.format} />
                <StatTile value={stats?.sessionCount} label={t("Stats.sessions")} loading={statsLoading} format={formatter.number.format} />
                <StatTile value={stats?.voteCount} label={t("Stats.votes")} loading={statsLoading} format={formatter.number.format} />
                <StatTile value={stats?.memberCount} label={t("Stats.members")} loading={statsLoading} format={formatter.number.format} />
            </div>
        </Card>

        <ContentSection title={t("Browse.heading")} subtitle={t("Browse.subheading")} level={1} className="home-browse">
            {BROWSE_GROUPS.filter((group) => grouped[group].length > 0).map((group, index) => {
                const groupStats = stats?.byGroup[group];
                const reversed = index % 2 === 1;

                return (
                    <div className={`home-group-row${reversed ? " home-group-row--reversed" : ""}`} key={group}>
                        <div className="home-group-gallery">
                            {grouped[group].map((item) => (
                                <Link
                                    key={item.text_id}
                                    to={`/${item.text_id}`}
                                    className="home-gallery-logo"
                                    title={municipalityLabel(item)}
                                    style={item.barva ? ({"--tile-accent": item.barva} as CSSProperties) : undefined}
                                >
                                    <img src={logoUrl(item.text_id)} alt={municipalityLabel(item)} />
                                </Link>
                            ))}
                        </div>

                        <div className="home-group-text">
                            <div className="home-group-head">
                                <h3>{groupLabels[group]}</h3>
                                <span className="home-group-count">{grouped[group].length}</span>
                            </div>
                            <ul className="home-group-stat-list">
                                <GroupStatLine value={groupStats?.sessionCount} label={t("Stats.sessions")} loading={statsLoading} format={formatter.number.format} />
                                <GroupStatLine value={groupStats?.voteCount} label={t("Stats.votes")} loading={statsLoading} format={formatter.number.format} />
                                <GroupStatLine value={groupStats?.memberCount} label={t("Stats.members")} loading={statsLoading} format={formatter.number.format} />
                            </ul>
                        </div>
                    </div>
                );
            })}
        </ContentSection>

        <ContentSection
            title={t("AnalysisCta.text")}
            level={1}
            className="home-analysis-cta"
            showMoreChevronLinkProps={{ text: t("AnalysisCta.link"), link: "/stats" }}
        />
    </>);
};

export {AllMunicipalitiesHome};
